import React, { useState, useEffect } from 'react';
import { ListChecks, ChevronDown, Check, Clock, Bell, Timer } from 'lucide-react';
import { Task } from '../../lib/tasks';
import { ViewChecklistModal } from './ViewChecklistModal';
import { getTaskChecklist, isChecklistCompleted } from '../../lib/checklists';
import { toast } from 'react-hot-toast';
import { useTasks } from '../../contexts/TasksContext';
import { useUnreadTasks } from '../../contexts/UnreadTasksContext';
import { useNotifications } from '../../contexts/NotificationsContext';

interface TasksDropdownProps {
  isOpen: boolean;
  onClose: () => void;
}

interface TaskWithChecklist extends Task {
  hasChecklist?: boolean;
}

export default function TasksDropdown({ isOpen, onClose }: TasksDropdownProps) {
  const { tasks, updateTask } = useTasks();
  const { unreadCount, markAllAsRead, unreadTasks } = useUnreadTasks();
  const { notifications } = useNotifications();
  const [selectedTaskId, setSelectedTaskId] = useState<string | null>(null);
  const [showChecklistModal, setShowChecklistModal] = useState(false);
  const [tasksWithChecklists, setTasksWithChecklists] = useState<Set<string>>(new Set());
  const [taskWithError, setTaskWithError] = useState<string | null>(null);
  const [isValidating, setIsValidating] = useState(false);
  const [completedTaskId, setCompletedTaskId] = useState<string | null>(null);

  // Check for checklists when tasks change
  React.useEffect(() => {
    const checkForChecklists = async () => {
      const checklistPromises = pendingTasks.map(async (task) => {
        try {
          const checklist = await getTaskChecklist(task.id);
          return { taskId: task.id, hasChecklist: !!checklist };
        } catch (error) {
          console.error('Error checking for checklist:', error);
          return { taskId: task.id, hasChecklist: false };
        }
      });

      const results = await Promise.all(checklistPromises);
      const taskIdsWithChecklists = new Set(
        results
          .filter(result => result.hasChecklist)
          .map(result => result.taskId)
      );
      setTasksWithChecklists(taskIdsWithChecklists);
    };

    if (isOpen && pendingTasks.length > 0) {
      checkForChecklists();
    }
  }, [isOpen, tasks]);

  const handleClose = () => {
    markAllAsRead();
    setTaskWithError(null);
    onClose();
  };

  const handleTaskClick = async (task: Task) => {
    setSelectedTaskId(task.id);
    setShowChecklistModal(true);
  };

  const handleStatusChange = async (taskId: string, newStatus: string) => {
    setIsValidating(true);
    try {
      // Check if task has a checklist and if it's completed
      const isCompleted = await isChecklistCompleted(taskId);
      if (!isCompleted) {
        setTaskWithError(taskId);
        toast.error('Please complete all checklist items before marking as done', {
          duration: 3000
        });
        return;
      }

      await updateTask(taskId, newStatus);
      setTaskWithError(null);
      // Set the completed task ID to trigger animation
      setCompletedTaskId(taskId);
      // Remove the completed status after animation
      setTimeout(() => {
        setCompletedTaskId(null);
      }, 1000); // Match this with the CSS animation duration
    } catch (error) {
      console.error('Error updating task status:', error);
      toast.error('Failed to update task status');
    } finally {
      setIsValidating(false);
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority.toLowerCase()) {
      case 'high':
        return 'bg-red-100 text-red-800';
      case 'medium':
        return 'bg-yellow-100 text-yellow-800';
      case 'low':
        return 'bg-green-100 text-green-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (date: Date) => {
    return date.toLocaleDateString('en-US', { 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (!isOpen) return null;

  const pendingTasks = tasks
    .filter(task => task.status === 'pending')
    .sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime())
    .slice(0, 5);

  return (
    <>
      <style jsx>{`
        @keyframes slideComplete {
          0% {
            transform: scaleX(0);
            opacity: 0;
          }
          100% {
            transform: scaleX(1);
            opacity: 1;
          }
        }

        @keyframes checkmarkDrop {
          0% {
            transform: scale(0) rotate(-45deg);
            opacity: 0;
          }
          70% {
            transform: scale(1.2) rotate(-45deg);
            opacity: 1;
          }
          100% {
            transform: scale(1) rotate(-45deg);
            opacity: 1;
          }
        }

        .task-complete-overlay {
          position: absolute;
          inset: 0;
          background: linear-gradient(
            to right,
            rgba(34, 197, 94, 0.2),
            rgba(34, 197, 94, 0.1)
          );
          transform-origin: left;
          animation: slideComplete 0.5s ease-out forwards;
          display: flex;
          align-items: center;
          justify-content: center;
          pointer-events: none;
          z-index: 10;
        }

        .task-complete-overlay::after {
          content: '';
          display: block;
          width: 20px;
          height: 40px;
          border: solid white;
          border-width: 0 4px 4px 0;
          animation: checkmarkDrop 0.4s ease-out forwards 0.2s;
          opacity: 0;
          transform-origin: center;
          filter: drop-shadow(0 0 2px rgba(34, 197, 94, 0.5));
        }
      `}</style>
      <div 
        className="fixed inset-0 z-40"
        onClick={handleClose}
      />
      <div className="absolute right-0 mt-2 w-72 bg-white rounded-lg shadow-lg border border-gray-200 overflow-hidden z-50">
        <div className="max-h-[80vh] sm:max-h-[600px] overflow-y-auto">
          <div className="p-2.5 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-1.5">
                <div className="flex items-center gap-1.5">
                  <h3 className="text-xs font-medium text-gray-900">My Tasks</h3>
                  <span className="bg-gray-100 text-gray-700 text-xs font-medium px-1.5 py-0.5 rounded-full">
                    {pendingTasks.length}
                  </span>
                  {unreadCount > 0 && (
                    <span className="bg-red-100 text-red-600 text-xs font-medium px-1.5 py-0.5 rounded-full">
                      {unreadCount} new
                    </span>
                  )}
                </div>
              </div>
            </div>
          </div>

          <div className="divide-y divide-gray-200 max-h-[480px] overflow-y-auto">
            {pendingTasks.length === 0 ? (
              <div className="p-4 text-center text-gray-500 text-sm">
                No pending tasks
              </div>
            ) : (
              pendingTasks.map((task) => (
                <div 
                  key={task.id}
                  className="relative hover:bg-gray-50 transition-colors cursor-pointer overflow-hidden"
                  onClick={() => handleTaskClick(task)}
                >
                  {completedTaskId === task.id && (
                    <div className="task-complete-overlay" />
                  )}
                  <div className={`p-4 ${unreadTasks.some(t => t.id === task.id) ? 'pt-7' : ''} relative`}>
                    {unreadTasks.some(t => t.id === task.id) && (
                      <div className="absolute top-0 left-0 right-0 bg-blue-50 text-blue-700 text-xs font-medium py-1 px-4">
                        NEW
                      </div>
                    )}
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <h4 className="text-sm font-medium text-gray-900 truncate flex items-center gap-1">
                            {task.title}
                          </h4>
                          <div className="flex items-center gap-1.5">
                            {tasksWithChecklists.has(task.id) && (
                              <div className="relative group">
                                <ListChecks className={`h-4 w-4 ${taskWithError === task.id ? 'text-red-600 animate-pulse' : 'text-blue-600'} flex-shrink-0`} />
                                {taskWithError === task.id && (
                                  <div className="absolute top-full left-1/2 transform -translate-x-1/2 mt-2 px-2 py-1 bg-red-50 border border-red-200 text-red-700 text-xs rounded shadow-sm whitespace-nowrap z-20">
                                    Complete checklist first
                                    <div className="absolute top-0 left-1/2 transform -translate-x-1/2 -translate-y-1/2 rotate-45 w-2 h-2 bg-red-50 border-l border-t border-red-200"></div>
                                  </div>
                                )}
                              </div>
                            )}
                            {task.constraints?.disableClockOut && (
                              <div className="relative group">
                                <Timer className="h-4 w-4 text-red-600 flex-shrink-0" />
                                <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-1 px-2 py-1 bg-gray-900 text-white text-xs rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap">
                                  Clock out restricted
                                </div>
                              </div>
                            )}
                            <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${getPriorityColor(task.priority)}`}>
                              {task.priority}
                            </span>
                          </div>
                        </div>
                        {task.description && (
                          <p className="text-sm text-gray-500 line-clamp-1 mt-0.5">
                            {task.description}
                          </p>
                        )}
                        <div className="flex items-center gap-2 mt-2 text-xs text-gray-500">
                          <Clock className="h-3.5 w-3.5" />
                          <span>Due: {formatDate(new Date(task.due_date || task.created_at))}</span>
                        </div>
                      </div>
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleStatusChange(task.id, 'completed');
                        }}
                        className={`flex items-center justify-center h-6 w-6 rounded-full ${
                          taskWithError === task.id 
                            ? 'text-red-400 hover:text-red-600 hover:bg-red-50' 
                            : 'text-gray-400 hover:text-green-600 hover:bg-green-100'
                        } transition-colors`}
                      >
                        <Check className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>

          <div className="p-3 border-t border-gray-200 bg-gray-50 rounded-b-lg">
            <a
              href="/tasks"
              className="block w-full text-center text-xs text-gray-600 hover:text-gray-900"
            >
              View all tasks
            </a>
          </div>
        </div>
      </div>

      {showChecklistModal && selectedTaskId && (
        <ViewChecklistModal
          taskId={selectedTaskId}
          onClose={() => setShowChecklistModal(false)}
        />
      )}
    </>
  );
} 